import Table from "@commons/Table/TableContent";
import {
  collarLogColumns,
  type CollarLogWithLabels,
} from "@typings/entities/devices/collar/collarLog/table";
import { useState, useEffect } from "react";
import { config } from "@/config";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type { CollarLog } from "@typings/entities/devices/collar/collarLog";
import { formatCoordinates, formatDateTime } from "@utils/formatters";
import { useGetCollarLogs } from "@services/individuals/listLog";
import { AppError } from "@utils/errors/appError";

function prepareGps(items: CollarLog[]): CollarLogWithLabels[] {
  return items.map((item) => ({
    ...item,
    id: item.recordedAt || "-",
    batteryLevel: item.batteryLevel ? `${item.batteryLevel}%` : "-",
    recordedAt: formatDateTime(item.recordedAt) || "-",
    coordinates: formatCoordinates(item.latitude, item.longitude) || "-",
  }));
}

function GpsTableView() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useGetCollarLogs(id ?? "", {
    page: page + 1,
    limit: rowsPerPage,
  });

  useEffect(() => {
    if (error && !isLoading) {
      const message =
        error instanceof AppError
          ? error.message
          : "Erro ao carregar eventos do GPS. Tente novamente";
      toast.error(message);
    }
  }, [error, isLoading]);

  const heads = collarLogColumns;

  const rows = prepareGps(data?.items || []);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-8">
        <ErrorOutlineIcon className="text-red-500 mb-2 text-6xl" />
        <span className="text-red-600 text-center">
          Ocorreu um erro ao carregar os eventos do GPS.
        </span>
      </div>
    );
  }

  if (!data) return null;

  return (
    <Table.Root isLoading={isLoading}>
      <Table.Head heads={heads} />
      <Table.Body<CollarLogWithLabels>
        rows={rows}
        columns={heads.map((head) => head.id)}
      />
      <Table.Footer
        colSpan={heads.length}
        count={data?.meta?.totalItems ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Table.Root>
  );
}

export default GpsTableView;
